// SAD-768: reviewer-authored "Add a missing line" / "Correct this line" on an
// open ContractIntake or Modification set. Stages a review item with a required
// reason; money writes only when the set is applied (D7).
//
// Price is the line's VALUE (extended price). Funding is a separate, optional
// field — never inferred from price, never defaulted (H1, M4).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraOverlayDrawer, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useIsViewOnly } from '../../../../../contexts/authContext';
import { useStageContractLineItem } from '../../../PreAward/awardCompareHooks';
import { optionalTaskHooks, popHooks } from '../../../Programs/operationalHooks';
import { PRICING_TYPE_OPTIONS } from '../../constants/pricingTypes';
import { optionalTaskLabel } from '../../lib/recordLabels';
export function StageContractLineDrawer({ open, onOpenChange, mode: initialMode, programId, changeSetId, initialClinNumber = '', initialTitle = '' }) {
    const isViewOnly = useIsViewOnly();
    const stage = useStageContractLineItem();
    const { data: periodsResp } = popHooks.useList({
        programId
    }, {
        enabled: open
    });
    const { data: tasksResp } = optionalTaskHooks.useList({
        programId
    }, {
        enabled: open
    });
    const periods = periodsResp?.items ?? [];
    const tasks = tasksResp?.items ?? [];
    const [mode, setMode] = useState(initialMode);
    const [clinNumber, setClinNumber] = useState(initialClinNumber);
    const [title, setTitle] = useState(initialTitle);
    const [periodId, setPeriodId] = useState('');
    const [optionalTaskNumber, setOptionalTaskNumber] = useState('');
    // No hidden FFP default (M4): contract type is chosen explicitly or left unset.
    const [contractType, setContractType] = useState('');
    const [price, setPrice] = useState('');
    const [fundedAmount, setFundedAmount] = useState('');
    const [reason, setReason] = useState('');
    const [redirectClin, setRedirectClin] = useState('');
    const [formError, setFormError] = useState('');
    useEffect(()=>{
        if (!open) return;
        setMode(initialMode);
        setClinNumber(initialClinNumber);
        setTitle(initialTitle);
        setPeriodId('');
        setOptionalTaskNumber('');
        setContractType('');
        setPrice('');
        setFundedAmount('');
        setReason('');
        setRedirectClin('');
        setFormError('');
    }, [
        open,
        initialMode,
        initialClinNumber,
        initialTitle
    ]);
    const reasonOk = reason.trim().length > 0;
    const clinOk = clinNumber.trim().length > 0;
    const titleOk = mode === 'correct' ? title.trim().length > 0 : true;
    const placeOk = mode === 'correct' || periodId !== '' || optionalTaskNumber !== '';
    const canSubmit = !isViewOnly && reasonOk && clinOk && titleOk && placeOk && !stage.isPending;
    const submit = async ()=>{
        setFormError('');
        if (!reason.trim()) {
            setFormError('A reason is required.');
            return;
        }
        if (mode === 'add' && !periodId && !optionalTaskNumber) {
            setFormError('Choose a period or an optional task.');
            return;
        }
        const resp = await stage.mutateAsync({
            changeSetId,
            action: mode,
            clinNumber: clinNumber.trim(),
            title: title.trim(),
            periodId: periodId || undefined,
            optionalTaskNumber: optionalTaskNumber ? Number(optionalTaskNumber) : undefined,
            contractType: contractType || undefined,
            price: price.trim() || undefined,
            fundedAmount: fundedAmount.trim() || undefined,
            reason: reason.trim()
        });
        if (resp?.redirectToCorrect) {
            setRedirectClin(resp.redirectToCorrect);
            setMode('correct');
            setClinNumber(resp.redirectToCorrect);
            // M4: a redirect must not carry Add's contract-type guess into Correct.
            setContractType('');
            return;
        }
        onOpenChange(false);
    };
    const titleText = mode === 'add' ? 'Add a missing line' : 'Correct this line';
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open,
        onOpenChange: (next)=>{
            if (!next && stage.isPending) return;
            onOpenChange(next);
        },
        title: titleText,
        description: "Staged on this review set. Values apply when you commit — nothing is written to the contract until then.",
        testId: Testids.reviewItem.stageLineDrawer,
        width: "lg",
        scrollBody: false,
        bodyProps: {
            className: 'flex flex-col'
        },
        footer: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-4 py-3 sm:px-5",
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    size: "sm",
                    disabled: stage.isPending,
                    onClick: ()=>onOpenChange(false),
                    children: "Cancel"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex-1"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    disabled: !canSubmit,
                    onClick: ()=>void submit(),
                    "data-testid": Testids.reviewItem.stageLineSubmit,
                    children: stage.isPending ? 'Saving…' : mode === 'add' ? 'Stage missing line' : 'Stage correction'
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:px-5",
            children: [
                redirectClin && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "warning",
                    variant: "framed",
                    title: "That line already exists",
                    "data-testid": Testids.reviewItem.stageLineRedirect,
                    "data-clin-number": redirectClin,
                    children: [
                        "Use Correct this line for ",
                        redirectClin,
                        " instead of adding it again."
                    ]
                }),
                formError && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "meta",
                    tone: "danger",
                    children: formError
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "CLIN number",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: clinNumber,
                        onChange: (e)=>setClinNumber(e.target.value),
                        placeholder: "0101AA",
                        disabled: mode === 'correct' && !!initialClinNumber && !redirectClin,
                        "data-testid": Testids.reviewItem.stageLineClinNumber
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    required: mode === 'correct',
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        placeholder: "Short title from the schedule",
                        "data-testid": Testids.reviewItem.stageLineTitle
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Period",
                    required: mode === 'add',
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: periodId,
                        onChange: (e)=>{
                            setPeriodId(e.target.value);
                            if (e.target.value) setOptionalTaskNumber('');
                        },
                        "data-testid": Testids.reviewItem.stageLinePeriod,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: mode === 'add' ? 'Choose a period…' : 'Leave unchanged…'
                            }),
                            periods.map((p)=>/*#__PURE__*/ _jsx("option", {
                                    value: p.id,
                                    children: p.periodName || p.periodType || p.id
                                }, p.id))
                        ]
                    })
                }),
                mode === 'add' && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Optional task",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: optionalTaskNumber,
                        onChange: (e)=>{
                            setOptionalTaskNumber(e.target.value);
                            if (e.target.value) setPeriodId('');
                        },
                        "data-testid": Testids.reviewItem.stageLineOptionalTask,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "None — use a period above"
                            }),
                            tasks.map((t)=>/*#__PURE__*/ _jsx("option", {
                                    value: String(t.taskNumber),
                                    children: t.name?.trim() ? `${optionalTaskLabel(String(t.taskNumber))} — ${t.name}` : optionalTaskLabel(String(t.taskNumber))
                                }, t.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Contract type",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: contractType,
                        onChange: (e)=>setContractType(e.target.value),
                        "data-testid": Testids.reviewItem.stageLineContractType,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: mode === 'add' ? 'Choose a type…' : 'Leave unchanged…'
                            }),
                            PRICING_TYPE_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.label
                                }, o.value))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Price",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: price,
                        onChange: (e)=>setPrice(e.target.value),
                        placeholder: "6401.70",
                        inputMode: "decimal",
                        "data-testid": Testids.reviewItem.stageLinePrice
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Funded amount",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: fundedAmount,
                        onChange: (e)=>setFundedAmount(e.target.value),
                        placeholder: "Leave blank when unstated",
                        inputMode: "decimal",
                        "data-testid": Testids.reviewItem.stageLineFunded
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Reason",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        placeholder: "What the award schedule shows, and why this change is needed",
                        "data-testid": Testids.reviewItem.stageLineReason
                    })
                })
            ]
        })
    });
}
